import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Mail, Plus, Send, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLOTS = Array.from({ length: 96 }, (_, i) => `${String(Math.floor(i / 4)).padStart(2, '0')}:${String((i % 4) * 15).padStart(2, '0')}`);
const label12 = (t) => { const [h, m] = t.split(':').map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`; };

const TimeSelect = ({ value, onChange, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="h-9 rounded-md border px-2 text-sm bg-white" data-testid={testId}>
    {SLOTS.map((s) => <option key={s} value={s}>{label12(s)}</option>)}
  </select>
);

const EmailSettingsForm = ({ token }) => {
  const [f, setF] = useState(null);
  const [recent, setRecent] = useState([]);
  const [busy, setBusy] = useState(false);
  const cfg = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    let live = true;
    axios.get(`${API}/reminder-settings`, cfg).then(({ data }) => {
      if (!live) return;
      setF({ email: data.email, enabled: data.enabled, times: data.times, summary_enabled: data.summary_enabled, summary_time: data.summary_time });
      setRecent(data.recent || []);
    }).catch(() => toast.error('Could not load email settings'));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!f) return <p className="text-sm text-slate-400">Loading…</p>;
  const set = (patch) => setF({ ...f, ...patch });

  const save = async () => {
    setBusy(true);
    try { await axios.put(`${API}/reminder-settings`, { ...f, email: f.email.trim() }, cfg); toast.success('Email settings saved'); }
    catch (e) { toast.error(e?.response?.status === 422 ? 'Check the email address and times' : 'Could not save'); }
    finally { setBusy(false); }
  };
  const test = async (kind) => {
    setBusy(true);
    try { const { data } = await axios.post(`${API}/reminder-settings/test?kind=${kind}`, {}, cfg); toast.success(`Test email sent to ${data.to}`); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not send test email'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="reminder-settings">
      <div className="space-y-1">
        <label className="text-sm font-semibold">Send emails to</label>
        <Input type="email" placeholder="doctor@example.com" value={f.email} onChange={(e) => set({ email: e.target.value })} className="h-10" data-testid="reminder-email-input" />
      </div>

      <div className="rounded-xl border p-4 space-y-3">
        <label className="flex items-center gap-2 font-semibold"><input type="checkbox" checked={f.enabled} onChange={(e) => set({ enabled: e.target.checked })} data-testid="reminder-enabled" /> Token status reminders</label>
        <p className="text-xs text-slate-500">Token counts and patients still waiting. Monday to Saturday.</p>
        <div className="flex flex-wrap gap-2" data-testid="reminder-times">
          {f.times.map((t, i) => (
            <div key={`${t}-${i}`} className="flex items-center gap-1">
              <TimeSelect value={t} onChange={(v) => set({ times: f.times.map((x, j) => (j === i ? v : x)) })} testId={`reminder-time-${i}`} />
              <button type="button" onClick={() => set({ times: f.times.filter((_, j) => j !== i) })} className="text-red-500 p-1" data-testid={`reminder-time-remove-${i}`}><X className="h-4 w-4" /></button>
            </div>
          ))}
          {f.times.length < 6 && (
            <Button type="button" size="sm" variant="outline" className="h-9" onClick={() => set({ times: [...f.times, '19:00'] })} data-testid="reminder-time-add"><Plus className="h-4 w-4 mr-1" /> Add time</Button>
          )}
        </div>
      </div>

      <div className="rounded-xl border p-4 space-y-3">
        <label className="flex items-center gap-2 font-semibold"><input type="checkbox" checked={f.summary_enabled} onChange={(e) => set({ summary_enabled: e.target.checked })} data-testid="summary-enabled" /> Evening closing summary</label>
        <p className="text-xs text-slate-500">Clinic, nursing and lab totals with losses. Sent once a day: as soon as the daily tally is signed, or at this time if it isn't signed yet (Mon–Sat).</p>
        <TimeSelect value={f.summary_time} onChange={(v) => set({ summary_time: v })} testId="summary-time" />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button disabled={busy} onClick={save} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="reminder-save">Save</Button>
        <Button variant="outline" disabled={busy || !f.email} onClick={() => test('token')} data-testid="reminder-test"><Send className="h-4 w-4 mr-1" /> Test token email</Button>
        <Button variant="outline" disabled={busy || !f.email} onClick={() => test('summary')} data-testid="summary-test"><Send className="h-4 w-4 mr-1" /> Test summary email</Button>
      </div>

      {recent.length > 0 && (
        <div className="text-xs text-slate-500 space-y-0.5" data-testid="reminder-recent">
          <p className="font-semibold text-slate-700">Recent emails</p>
          {recent.map((x) => <p key={x.run_id}>{x.kind === 'summary' ? 'Summary' : 'Token status'} • {x.status} • {new Date(x.sent_at || x.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true })}</p>)}
        </div>
      )}
    </div>
  );
};

export const EmailSettingsButton = ({ token }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)} variant="ghost" className="text-xs md:text-sm hover:bg-sky-50 text-sky-800 hover:text-sky-900" data-testid="email-settings-button">
        <Mail className="mr-1 md:mr-2 h-4 w-4" />
        <span className="hidden md:inline">Email</span>
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="email-settings-dialog">
          <DialogHeader><DialogTitle>Email Settings — Reminders &amp; Day Summary</DialogTitle></DialogHeader>
          {open && <EmailSettingsForm token={token} />}
        </DialogContent>
      </Dialog>
    </>
  );
};
